import { Button, Link } from '@portfolio/components'
import { styled } from '@portfolio/styles'
import { Error } from '@portfolio/icons'
import { useState, ChangeEvent, ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const PasswordProtectedContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'auto',
  gridTemplateRows: 'auto auto',
  gridGap: '$64',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$darkThemeBackground',
  padding: '$144 $20',
})

const LinkWrapper = styled('div', {
  display: 'grid',
  justifyContent: 'center',
})

const StyledLink = styled(Link, {
  width: 'fit-content',
  alignSelf: 'center',
})

const PasswordWrapper = styled('div', {
  backgroundColor: '$white',
  borderRadius: '$rBoxes',
  padding: '$32',
  width: '100%',
  maxWidth: '439px',
  display: 'grid',
  gridGap: '$32',
})

const Title = styled('p', {
  projectFont: 'heading08',
  color: '$textPrimaryBlack',
})

const Subtitle = styled('p', {
  projectFont: 'heading08',
  color: '$gray300',
})

const FormWrap = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '$16',
})

const InputWrap = styled('div', {
  display: 'grid',
})

const TextInput = styled('input', {
  width: '100%',
  height: '100%',
  borderRadius: '$r2',
  projectFont: 'body04',
  border: '1px solid $black_02',
  color: '$black',
  transition: 'color 250ms',
  padding: '$12 $20',
  textAlign: 'start',

  '&::placeholder': {
    color: '$black_06',
  },

  '&:focus-visible': {
    outline: '$focusBlue solid 3px;',
  },

  variants: {
    isError: {
      true: {
        borderColor: '$errorRed',
      },
    },
  },
})

const FormError = styled('div', {
  projectFont: 'ui04',
  color: '$errorRed',
  display: 'flex',
  flexDirection: 'row',
  columnGap: '$4',
  alignItems: 'center',
  justifyContent: 'flex-start',
  visibility: 'hidden',
  minHeight: '$24',

  variants: {
    isVisible: {
      true: {
        visibility: 'visible',
      },
    },
  },
})

const ErrorMessage = ({ message }: { message: string }) => (
  <FormError isVisible={!!message}>
    {!!message && (
      <>
        <Error />
        {message}
      </>
    )}
  </FormError>
)

export const PasswordProtected = ({
  isPasswordProtected,
  password,
  children,
}: {
  isPasswordProtected: boolean
  password: string
  children: ReactElement | ReactElement[]
}) => {
  const { t } = useTranslation('passwordProtected')
  const [passwordInput, setPasswordInput] = useState('')
  const [error, setError] = useState('')
  const [isPasswordValid, setIsPasswordValid] = useState(false)

  useEffect(() => {
    const root = document.body
    if (isPasswordValid || !isPasswordProtected) {
      root.style.setProperty('background-color', 'var(--main-bg-color)')
    } else {
      root.style.setProperty('background-color', '#121212')
    }
  }, [isPasswordValid, isPasswordProtected])

  const submitPassword = () => {
    if (passwordInput === password) {
      setIsPasswordValid(true)
    } else {
      setError(String(t('invalidPassword')))
    }
  }

  const validateField = (value: string) => {
    let error = ''

    if (!value.trim()) {
      error = t('required')
    }

    return error
  }

  const handlePasswordInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const error = validateField(value)
    setError(error)
    setPasswordInput(value)
  }

  return isPasswordValid || !isPasswordProtected ? (
    <>{children}</>
  ) : (
    <PasswordProtectedContainer>
      <LinkWrapper>
        <StyledLink appearance={'containedTransparent'} href="/work">
          Go Back
        </StyledLink>
      </LinkWrapper>
      <PasswordWrapper>
        <div>
          <Title>{t('title')}</Title>
          <Subtitle>{t('subtitle')}</Subtitle>
        </div>

        <FormWrap>
          <InputWrap>
            <TextInput
              type="password"
              placeholder={String(t('enterPassword'))}
              value={passwordInput}
              onChange={handlePasswordInput}
              isError={!!error}
            />
            <ErrorMessage message={error} />
          </InputWrap>
          <Button appearance={'containedBlack'} onClick={submitPassword}>
            {t('enterPassword')}
          </Button>
        </FormWrap>
      </PasswordWrapper>
    </PasswordProtectedContainer>
  )
}
